import React, { useEffect, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { getEUETSData } from "../../services/complianceService";
import BenchmarkYearPicker from "../../components/Common/BenchmarkYearPicker";
import ExportToExcel from "../../components/Common/ExportToExcel";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";

const EUETS = () => {
  const location = useLocation();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVesselId, setSelectedVesselId] = useState(null);
  const [mainSpreadsheetData, setMainSpreadsheetData] = useState([[]]);

  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { currentVesselId } = location.state || {};

  useEffect(() => {
    if (currentVesselId) {
      setSelectedVesselId(currentVesselId);
    } else if (currentVessel?._id) {
      setSelectedVesselId(currentVessel._id);
    }
  }, [currentVesselId, currentVessel?._id]);

  // In your frontend, modify the transformApiData function:

  const transformApiData = (data) => {
    const { fuelConsumption, emissions, etsCompliance, vessel } = data;
    const isContainerVessel = vessel.isContainerVessel;

    const mainData = [
      [
        { value: "Segment", readOnly: true, className: "header-cell" },
        {
          value: "Fuel Consumption HFO (MT)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "Fuel Consumption LFO (MT)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "Fuel Consumption MGO (MT)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CO2 Emission Factor (HFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CO2 Emission Factor (LFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CO2 Emission Factor (MGO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CH4 Emission Factor (HFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CH4 Emission Factor (LFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CH4 Emission Factor (MGO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "N2O Emission Factor (HFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "N2O Emission Factor (LFO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "N2O Emission Factor (MGO)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CO2 Emissions (MT)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "CH4 Emissions (MT)",
          readOnly: true,
          className: "header-cell",
        },
        {
          value: "N2O Emissions (MT)",
          readOnly: true,
          className: "header-cell",
        },
        { value: "ETS Coverage %", readOnly: true, className: "header-cell" },
        {
          value: "GHG under ETS (MT)",
          readOnly: true,
          className: "header-cell",
        },
        { value: "Reduction Factor", readOnly: true, className: "header-cell" },
        {
          value: "Ice Class Exemption",
          readOnly: true,
          className: "header-cell",
        },
        { value: "Base EUA", readOnly: true, className: "header-cell" },
        { value: "Final EUA", readOnly: true, className: "header-cell" },
      ],

      // Regular ETS Segments
      [
        { value: "At EEA Ports", readOnly: true, className: "segment-cell" },
        { value: fuelConsumption.bySegment.atEEAPorts.HFO || 0 },
        { value: fuelConsumption.bySegment.atEEAPorts.LFO || 0 },
        { value: fuelConsumption.bySegment.atEEAPorts.MGO || 0 },
        { value: emissions.factors.CO2.HFO || 0 },
        { value: emissions.factors.CO2.LFO || 0 },
        { value: emissions.factors.CO2.MGO || 0 },
        { value: emissions.factors.CH4.HFO || 0 },
        { value: emissions.factors.CH4.LFO || 0 },
        { value: emissions.factors.CH4.MGO || 0 },
        { value: emissions.factors.N2O.HFO || 0 },
        { value: emissions.factors.N2O.LFO || 0 },
        { value: emissions.factors.N2O.MGO || 0 },
        { value: emissions.bySegment.atEEAPorts.CO2_mt || 0 },
        { value: emissions.bySegment.atEEAPorts.CH4_mt || 0 },
        { value: emissions.bySegment.atEEAPorts.N2O_mt || 0 },
        { value: (etsCompliance.segments.atEEAPorts.etsPct || 0) * 100 },
        { value: etsCompliance.segments.atEEAPorts.ghgUnderETS || 0 },
        { value: etsCompliance.reductionFactor || 0 },
        { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
        { value: etsCompliance.segments.atEEAPorts.baseEUAs || 0 },
        { value: etsCompliance.segments.atEEAPorts.finalEUAs || 0 },
      ],

      [
        {
          value: "Between EEA Ports",
          readOnly: true,
          className: "segment-cell",
        },
        { value: fuelConsumption.bySegment.betweenEEAPorts.HFO || 0 },
        { value: fuelConsumption.bySegment.betweenEEAPorts.LFO || 0 },
        { value: fuelConsumption.bySegment.betweenEEAPorts.MGO || 0 },
        { value: emissions.factors.CO2.HFO || 0 },
        { value: emissions.factors.CO2.LFO || 0 },
        { value: emissions.factors.CO2.MGO || 0 },
        { value: emissions.factors.CH4.HFO || 0 },
        { value: emissions.factors.CH4.LFO || 0 },
        { value: emissions.factors.CH4.MGO || 0 },
        { value: emissions.factors.N2O.HFO || 0 },
        { value: emissions.factors.N2O.LFO || 0 },
        { value: emissions.factors.N2O.MGO || 0 },
        { value: emissions.bySegment.betweenEEAPorts.CO2_mt || 0 },
        { value: emissions.bySegment.betweenEEAPorts.CH4_mt || 0 },
        { value: emissions.bySegment.betweenEEAPorts.N2O_mt || 0 },
        { value: (etsCompliance.segments.betweenEEAPorts.etsPct || 0) * 100 },
        { value: etsCompliance.segments.betweenEEAPorts.ghgUnderETS || 0 },
        { value: etsCompliance.reductionFactor || 0 },
        { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
        { value: etsCompliance.segments.betweenEEAPorts.baseEUAs || 0 },
        { value: etsCompliance.segments.betweenEEAPorts.finalEUAs || 0 },
      ],

      [
        {
          value: "Departed EEA → Non-EEA",
          readOnly: true,
          className: "segment-cell",
        },
        { value: fuelConsumption.bySegment.departedEEAtoNonEEA.HFO || 0 },
        { value: fuelConsumption.bySegment.departedEEAtoNonEEA.LFO || 0 },
        { value: fuelConsumption.bySegment.departedEEAtoNonEEA.MGO || 0 },
        { value: emissions.factors.CO2.HFO || 0 },
        { value: emissions.factors.CO2.LFO || 0 },
        { value: emissions.factors.CO2.MGO || 0 },
        { value: emissions.factors.CH4.HFO || 0 },
        { value: emissions.factors.CH4.LFO || 0 },
        { value: emissions.factors.CH4.MGO || 0 },
        { value: emissions.factors.N2O.HFO || 0 },
        { value: emissions.factors.N2O.LFO || 0 },
        { value: emissions.factors.N2O.MGO || 0 },
        { value: emissions.bySegment.departedEEAtoNonEEA.CO2_mt || 0 },
        { value: emissions.bySegment.departedEEAtoNonEEA.CH4_mt || 0 },
        { value: emissions.bySegment.departedEEAtoNonEEA.N2O_mt || 0 },
        {
          value: (etsCompliance.segments.departedEEAtoNonEEA.etsPct || 0) * 100,
        },
        { value: etsCompliance.segments.departedEEAtoNonEEA.ghgUnderETS || 0 },
        { value: etsCompliance.reductionFactor || 0 },
        { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
        { value: etsCompliance.segments.departedEEAtoNonEEA.baseEUAs || 0 },
        { value: etsCompliance.segments.departedEEAtoNonEEA.finalEUAs || 0 },
      ],

      [
        {
          value: "Departed Non-EEA → EEA",
          readOnly: true,
          className: "segment-cell",
        },
        { value: fuelConsumption.bySegment.departedNonEEAtoEEA.HFO || 0 },
        { value: fuelConsumption.bySegment.departedNonEEAtoEEA.LFO || 0 },
        { value: fuelConsumption.bySegment.departedNonEEAtoEEA.MGO || 0 },
        { value: emissions.factors.CO2.HFO || 0 },
        { value: emissions.factors.CO2.LFO || 0 },
        { value: emissions.factors.CO2.MGO || 0 },
        { value: emissions.factors.CH4.HFO || 0 },
        { value: emissions.factors.CH4.LFO || 0 },
        { value: emissions.factors.CH4.MGO || 0 },
        { value: emissions.factors.N2O.HFO || 0 },
        { value: emissions.factors.N2O.LFO || 0 },
        { value: emissions.factors.N2O.MGO || 0 },
        { value: emissions.bySegment.departedNonEEAtoEEA.CO2_mt || 0 },
        { value: emissions.bySegment.departedNonEEAtoEEA.CH4_mt || 0 },
        { value: emissions.bySegment.departedNonEEAtoEEA.N2O_mt || 0 },
        {
          value: (etsCompliance.segments.departedNonEEAtoEEA.etsPct || 0) * 100,
        },
        { value: etsCompliance.segments.departedNonEEAtoEEA.ghgUnderETS || 0 },
        { value: etsCompliance.reductionFactor || 0 },
        { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
        { value: etsCompliance.segments.departedNonEEAtoEEA.baseEUAs || 0 },
        { value: etsCompliance.segments.departedNonEEAtoEEA.finalEUAs || 0 },
      ],

      // Transhipment Segments (Only for Container Vessels)
      ...(isContainerVessel
        ? [
            // Section Header for Transhipment
            [
              {
                value: "TRANSHIPMENT PORT SEGMENTS (Container Vessels Only)",
                readOnly: true,
                className: "section-header",
                colSpan: 21,
              },
              ...Array(20).fill({
                value: "",
                readOnly: true,
                className: "section-header",
              }),
            ],
            [
              {
                value: "Non-EEA → Transhipment Port",
                readOnly: true,
                className: "transhipment-cell",
              },
              {
                value: fuelConsumption.bySegment.nonEEAToTranshipment?.HFO || 0,
              },
              {
                value: fuelConsumption.bySegment.nonEEAToTranshipment?.LFO || 0,
              },
              {
                value: fuelConsumption.bySegment.nonEEAToTranshipment?.MGO || 0,
              },
              { value: emissions.factors.CO2.HFO || 0 },
              { value: emissions.factors.CO2.LFO || 0 },
              { value: emissions.factors.CO2.MGO || 0 },
              { value: emissions.factors.CH4.HFO || 0 },
              { value: emissions.factors.CH4.LFO || 0 },
              { value: emissions.factors.CH4.MGO || 0 },
              { value: emissions.factors.N2O.HFO || 0 },
              { value: emissions.factors.N2O.LFO || 0 },
              { value: emissions.factors.N2O.MGO || 0 },
              { value: emissions.bySegment.nonEEAToTranshipment?.CO2_mt || 0 },
              { value: emissions.bySegment.nonEEAToTranshipment?.CH4_mt || 0 },
              { value: emissions.bySegment.nonEEAToTranshipment?.N2O_mt || 0 },
              {
                value:
                  (etsCompliance.segments.nonEEAToTranshipment?.etsPct || 0) *
                  100,
              },
              {
                value:
                  etsCompliance.segments.nonEEAToTranshipment?.ghgUnderETS || 0,
              },
              { value: etsCompliance.reductionFactor || 0 },
              { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
              {
                value:
                  etsCompliance.segments.nonEEAToTranshipment?.baseEUAs || 0,
              },
              {
                value:
                  etsCompliance.segments.nonEEAToTranshipment?.finalEUAs || 0,
              },
            ],
            [
              {
                value: "Transhipment Port → EEA",
                readOnly: true,
                className: "transhipment-cell",
              },
              { value: fuelConsumption.bySegment.transhipmentToEEA?.HFO || 0 },
              { value: fuelConsumption.bySegment.transhipmentToEEA?.LFO || 0 },
              { value: fuelConsumption.bySegment.transhipmentToEEA?.MGO || 0 },
              { value: emissions.factors.CO2.HFO || 0 },
              { value: emissions.factors.CO2.LFO || 0 },
              { value: emissions.factors.CO2.MGO || 0 },
              { value: emissions.factors.CH4.HFO || 0 },
              { value: emissions.factors.CH4.LFO || 0 },
              { value: emissions.factors.CH4.MGO || 0 },
              { value: emissions.factors.N2O.HFO || 0 },
              { value: emissions.factors.N2O.LFO || 0 },
              { value: emissions.factors.N2O.MGO || 0 },
              { value: emissions.bySegment.transhipmentToEEA?.CO2_mt || 0 },
              { value: emissions.bySegment.transhipmentToEEA?.CH4_mt || 0 },
              { value: emissions.bySegment.transhipmentToEEA?.N2O_mt || 0 },
              {
                value:
                  (etsCompliance.segments.transhipmentToEEA?.etsPct || 0) * 100,
              },
              {
                value:
                  etsCompliance.segments.transhipmentToEEA?.ghgUnderETS || 0,
              },
              { value: etsCompliance.reductionFactor || 0 },
              { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
              {
                value: etsCompliance.segments.transhipmentToEEA?.baseEUAs || 0,
              },
              {
                value: etsCompliance.segments.transhipmentToEEA?.finalEUAs || 0,
              },
            ],
            [
              {
                value: "EEA → Transhipment Port",
                readOnly: true,
                className: "transhipment-cell",
              },
              { value: fuelConsumption.bySegment.EEAToTranshipment?.HFO || 0 },
              { value: fuelConsumption.bySegment.EEAToTranshipment?.LFO || 0 },
              { value: fuelConsumption.bySegment.EEAToTranshipment?.MGO || 0 },
              { value: emissions.factors.CO2.HFO || 0 },
              { value: emissions.factors.CO2.LFO || 0 },
              { value: emissions.factors.CO2.MGO || 0 },
              { value: emissions.factors.CH4.HFO || 0 },
              { value: emissions.factors.CH4.LFO || 0 },
              { value: emissions.factors.CH4.MGO || 0 },
              { value: emissions.factors.N2O.HFO || 0 },
              { value: emissions.factors.N2O.LFO || 0 },
              { value: emissions.factors.N2O.MGO || 0 },
              { value: emissions.bySegment.EEAToTranshipment?.CO2_mt || 0 },
              { value: emissions.bySegment.EEAToTranshipment?.CH4_mt || 0 },
              { value: emissions.bySegment.EEAToTranshipment?.N2O_mt || 0 },
              {
                value:
                  (etsCompliance.segments.EEAToTranshipment?.etsPct || 0) * 100,
              },
              {
                value:
                  etsCompliance.segments.EEAToTranshipment?.ghgUnderETS || 0,
              },
              { value: etsCompliance.reductionFactor || 0 },
              { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
              {
                value: etsCompliance.segments.EEAToTranshipment?.baseEUAs || 0,
              },
              {
                value: etsCompliance.segments.EEAToTranshipment?.finalEUAs || 0,
              },
            ],
            [
              {
                value: "Transhipment Port → Non-EEA",
                readOnly: true,
                className: "transhipment-cell",
              },
              {
                value: fuelConsumption.bySegment.transhipmentToNonEEA?.HFO || 0,
              },
              {
                value: fuelConsumption.bySegment.transhipmentToNonEEA?.LFO || 0,
              },
              {
                value: fuelConsumption.bySegment.transhipmentToNonEEA?.MGO || 0,
              },
              { value: emissions.factors.CO2.HFO || 0 },
              { value: emissions.factors.CO2.LFO || 0 },
              { value: emissions.factors.CO2.MGO || 0 },
              { value: emissions.factors.CH4.HFO || 0 },
              { value: emissions.factors.CH4.LFO || 0 },
              { value: emissions.factors.CH4.MGO || 0 },
              { value: emissions.factors.N2O.HFO || 0 },
              { value: emissions.factors.N2O.LFO || 0 },
              { value: emissions.factors.N2O.MGO || 0 },
              { value: emissions.bySegment.transhipmentToNonEEA?.CO2_mt || 0 },
              { value: emissions.bySegment.transhipmentToNonEEA?.CH4_mt || 0 },
              { value: emissions.bySegment.transhipmentToNonEEA?.N2O_mt || 0 },
              {
                value:
                  (etsCompliance.segments.transhipmentToNonEEA?.etsPct || 0) *
                  100,
              },
              {
                value:
                  etsCompliance.segments.transhipmentToNonEEA?.ghgUnderETS || 0,
              },
              { value: etsCompliance.reductionFactor || 0 },
              { value: etsCompliance.iceClassExemption ? "Yes" : "No" },
              {
                value:
                  etsCompliance.segments.transhipmentToNonEEA?.baseEUAs || 0,
              },
              {
                value:
                  etsCompliance.segments.transhipmentToNonEEA?.finalEUAs || 0,
              },
            ],
          ]
        : []),

      // Totals Row
      [
        { value: "TOTALS", readOnly: true, className: "totals-cell" },
        { value: fuelConsumption.total.HFO || 0, className: "totals-value" },
        { value: fuelConsumption.total.LFO || 0, className: "totals-value" },
        { value: fuelConsumption.total.MGO || 0, className: "totals-value" },
        { value: emissions.factors.CO2.HFO || 0, className: "totals-value" },
        { value: emissions.factors.CO2.LFO || 0, className: "totals-value" },
        { value: emissions.factors.CO2.MGO || 0, className: "totals-value" },
        { value: emissions.factors.CH4.HFO || 0, className: "totals-value" },
        { value: emissions.factors.CH4.LFO || 0, className: "totals-value" },
        { value: emissions.factors.CH4.MGO || 0, className: "totals-value" },
        { value: emissions.factors.N2O.HFO || 0, className: "totals-value" },
        { value: emissions.factors.N2O.LFO || 0, className: "totals-value" },
        { value: emissions.factors.N2O.MGO || 0, className: "totals-value" },
        { value: emissions.totals.CO2_mt || 0, className: "totals-value" },
        { value: emissions.totals.CH4_mt || 0, className: "totals-value" },
        { value: emissions.totals.N2O_mt || 0, className: "totals-value" },
        {
          value: "Total ETS Coverage",
          readOnly: true,
          className: "formula-cell",
        },
        {
          value: etsCompliance.totals.totalGHGUnderETS || 0,
          className: "totals-value",
        },
        {
          value: etsCompliance.reductionFactor || 0,
          className: "totals-value",
        },
        {
          value: etsCompliance.iceClassExemption ? "Yes" : "No",
          className: "totals-value",
        },
        {
          value: etsCompliance.totals.totalBaseEUAs || 0,
          className: "totals-value",
        },
        {
          value: etsCompliance.totals.totalFinalEUAs || 0,
          className: "totals-value",
        },
      ],
    ];

    return {
      mainData,
    };
  };

  const benchmarkYear = useSelector((state) => state.benchmarkYear.year);

  useEffect(() => {
    if (!selectedVesselId) return;

    const fetchEUMRVData = async () => {
      console.log("benchmarkYear:", benchmarkYear);
      setLoading(true);
      try {
        const res = await getEUETSData(selectedVesselId, benchmarkYear);
        if (res.status === "success") {
          setApiData(res.data);
          const transformedData = transformApiData(res.data);
          setMainSpreadsheetData(transformedData.mainData);
        } else {
          toast.error("Failed to load EU MRV data");
          setApiData(null);
        }
      } catch (err) {
        console.error("Error fetching vessel data:", err);
        toast.error("Error loading EU ETS data");
        setApiData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEUMRVData();
  }, [selectedVesselId, benchmarkYear]);

  const handleMainSpreadsheetChange = (data) => {
    setMainSpreadsheetData(data);
  };

  const hasFuelConsumptionData = (apiData) => {
    if (!apiData || !apiData.fuelConsumption) return false;
    
    const { fuelConsumption } = apiData;
    
    const totalFuel = fuelConsumption.total || {};
    if ((totalFuel.HFO || 0) > 0 || (totalFuel.LFO || 0) > 0 || (totalFuel.MGO || 0) > 0) {
      return true;
    }
    
    const segments = fuelConsumption.bySegment || {};
    
    const regularSegments = [
      'atEEAPorts',
      'betweenEEAPorts', 
      'departedEEAtoNonEEA',
      'departedNonEEAtoEEA'
    ];
    
    for (const segment of regularSegments) {
      if (segments[segment]) {
        const segmentData = segments[segment];
        if ((segmentData.HFO || 0) > 0 || (segmentData.LFO || 0) > 0 || (segmentData.MGO || 0) > 0) {
          return true;
        }
      }
    }
    
    const transhipmentSegments = [
      'nonEEAToTranshipment',
      'transhipmentToEEA',
      'EEAToTranshipment',
      'transhipmentToNonEEA'
    ];
    
    for (const segment of transhipmentSegments) {
      if (segments[segment]) {
        const segmentData = segments[segment];
        if ((segmentData.HFO || 0) > 0 || (segmentData.LFO || 0) > 0 || (segmentData.MGO || 0) > 0) {
          return true;
        }
      }
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="EU ETS Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading EU ETS data...</span>
        </div>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="EU ETS Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-500">No EU ETS data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans text-sm min-h-screen bg-gray-50">
      <div className="flex items-center justify-between p-4 border-b border-gray-300">
        <VesselHeader pageTitle="EU ETS Compliance Data" />
        <div className="flex space-x-2">
          <BenchmarkYearPicker />
          {apiData && hasFuelConsumptionData(apiData) && <ExportToExcel />}
        </div>
      </div>

      <div className="p-4 space-y-6">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold">
              Fuel Consumption, Emissions, and ETS Calculations
            </h2>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[2200px] p-4">
              <Spreadsheet
                data={mainSpreadsheetData}
                onChange={handleMainSpreadsheetChange}
                hideRowIndicators={true}
                hideColumnIndicators={true}
                className="enhanced-spreadsheet"
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .enhanced-spreadsheet {
          --selected-cell: #e3f2fd;
          --selected-row: #f5f5f5;
          --selected-column: #f5f5f5;
        }
        .enhanced-spreadsheet table {
          border-collapse: separate;
          border-spacing: 0;
          width: auto !important;
        }
        .enhanced-spreadsheet td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 8px 12px !important;
          transition: background-color 0.2s;
          white-space: nowrap;
          color: #1f2937 !important;
          background-color: white !important;
        }
        .enhanced-spreadsheet td:first-child {
          border-left: 1px solid #e5e7eb !important;
        }
        .header-cell {
          background-color: #f1f5f9 !important;
          font-weight: 700 !important;
          color: #374151 !important;
        }

        .segment-cell {
          background-color: #f8fafc !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
        }

        .formula-cell {
          background-color: #f3f4f6 !important;
          font-style: italic !important;
          color: #6b7280 !important;
          font-size: 0.9em !important;
        }

        .section-header {
          background-color: #1e40af !important;
          color: white !important;
          font-weight: 700 !important;
          font-size: 1.1em !important;
          text-align: center !important;h
        }

        .totals-cell {
          background-color: #dcfce7 !important;
          font-weight: 800 !important;
          color: #166534 !important;
        }

        .totals-value {
          background-color: #bbf7d0 !important;
          font-weight: 700 !important;
          color: #166534 !important;
        }

        .enhanced-spreadsheet tr:hover td {
          background-color: #f9fafb !important;
        }

        .enhanced-spreadsheet .active-cell {
          box-shadow: inset 0 0 0 2px #3b82f6 !important;
        }

        .transhipment-cell {
          background-color: #fff7ed !important;
          font-weight: 600 !important;
          color: #ea580c !important;
          border-left: 3px solid #ea580c !important;
        }

        .section-header {
          background-color: #7c2d12 !important;
          color: white !important;
          font-weight: 700 !important;
          font-size: 1.1em !important;
          text-align: center !important;
        }
      `}</style>
    </div>
  );
};

export default EUETS;
